import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from './api';

export interface EntryTag {
  id: string;
  name: string;
  color: string | null;
}

export interface WeekEntry {
  id: string;
  workDate: string;
  begin: string;
  end: string | null;
  durationSeconds: number | null;
  description: string | null;
  billable: boolean;
  exported: boolean;
  /** Rate overrides in minor units, as strings. Null means "use the rate tables". */
  hourlyRateMinor: string | null;
  fixedRateMinor: string | null;
  project: { id: string; name: string; customer: { name: string } };
  activity: { id: string; name: string };
  tags: EntryTag[];
}

export interface WeekDay {
  date: string;
  /** Decided by the API from the lockdown boundary; never recomputed here. */
  locked: boolean;
  totalSeconds: number;
  entries: WeekEntry[];
}

/** Who decided a week, when, and what they said about it. */
export interface WeekDecision {
  status: 'APPROVED' | 'DENIED';
  message: string | null;
  by: string | null;
  at: string;
}

/**
 * One submittable part of a week. A week inside one month is one part, the whole
 * week; a week crossing a month end is two, split where the month changes, and
 * each is submitted and decided on its own.
 */
export interface WeekPeriod {
  start: string;
  end: string;
  status: 'NOT_SUBMITTED' | 'SUBMITTED' | 'APPROVED' | 'DENIED';
  /** The decision behind that status, while it still applies. Null before one. */
  decision: WeekDecision | null;
  totalSeconds: number;
  expectedSeconds: number;
}

export interface Week {
  employee: { id: string; displayName: string };
  weekStart: string;
  weekEnd: string;
  /** Submission state per part, from the same response as the lock flags. */
  periods: WeekPeriod[];
  totalSeconds: number;
  days: WeekDay[];
}

export function useWeek(date: string, employeeId?: string, enabled = true) {
  return useQuery({
    // `enabled` gates fetching only - the week is rendered by whichever view is
    // showing, and the inactive one should not cost a request.
    enabled,
    queryKey: ['timesheet', 'week', date, employeeId ?? 'me'],
    queryFn: () =>
      api.get<Week>(
        `/timesheet/week?date=${date}${employeeId ? `&employeeId=${employeeId}` : ''}`,
      ),
    // The mirror only changes when the migration runs, so a short cache is plenty
    // and stops the week flickering as the user pages back and forth.
    staleTime: 30_000,
  });
}

export interface EntryDraft {
  workDate: string;
  projectId: string;
  activityId: string;
  durationSeconds: number;
  /** `HH:MM` in the person's own day. Absent uses the configured start time. */
  beginTime?: string;
  description?: string | null;
  tagIds?: string[];
  /**
   * Rate overrides as decimal strings ("1500" or "1500.50"), never numbers.
   * `null` clears an override; absent leaves whatever the entry has.
   */
  hourlyRate?: string | null;
  fixedRate?: string | null;
}

/**
 * Every mutation invalidates the whole timesheet cache rather than patching the
 * affected week by hand. Totals, day locks and the entries themselves all come
 * from the same response, and keeping three of them in step client-side is how
 * they start disagreeing with the server.
 */
function useTimesheetMutation<TArgs, TResult = unknown>(fn: (args: TArgs) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['timesheet'] }),
  });
}

export const useCreateEntry = () =>
  useTimesheetMutation((draft: EntryDraft) => api.post('/timesheet/entries', draft));

export const useUpdateEntry = () =>
  useTimesheetMutation(({ id, ...patch }: Partial<EntryDraft> & { id: string }) =>
    api.patch(`/timesheet/entries/${id}`, patch),
  );

export const useDeleteEntry = () =>
  useTimesheetMutation((id: string) => api.delete(`/timesheet/entries/${id}`));

export interface BulkResult {
  created: number;
  skippedLocked: number;
  /** Days the person's contract does not work. */
  skippedNonWorking: number;
  skippedHoliday: number;
  skippedAbsence: number;
  skippedOutsideProject: number;
  /** Days of the week that were not ticked. Not a problem - just not asked for. */
  skippedNotChosen: number;
  firstLockedDay: string | null;
  firstHoliday: string | null;
}

export const useBulkInsert = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: Omit<EntryDraft, 'workDate'> & {
      from: string;
      to: string;
      includeWeekends?: boolean;
      /** Calendar weekdays to fill, 0 = Sunday. Narrows; never widens. */
      weekdays?: number[];
    }) => api.post<BulkResult>('/timesheet/bulk', params),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['timesheet'] }),
  });
};

/** The entry whose clock is running, or null. */
export interface RunningEntry {
  id: string;
  begin: string;
  workDate: string;
  description: string | null;
  project: { id: string; name: string; customer: { name: string } };
  activity: { id: string; name: string };
}

export const useRunningEntry = () =>
  useQuery({
    queryKey: ['timesheet', 'running'],
    queryFn: () => api.get<RunningEntry | null>('/timesheet/entries/running'),
    // The elapsed time is counted in the browser; this only has to notice a clock
    // started or stopped somewhere else, which is not a per-second question.
    refetchInterval: 60_000,
  });

export const useStartEntry = () =>
  useTimesheetMutation((input: { projectId: string; activityId: string; description?: string | null }) =>
    api.post<{ id: string; begin: string }>('/timesheet/entries/start', input),
  );

export const useStopEntry = () =>
  useTimesheetMutation((id: string) =>
    api.post<{ recorded: boolean; durationSeconds?: number; message?: string }>(
      `/timesheet/entries/${id}/stop`,
      {},
    ),
  );
